import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input } from '@angular/core';
import {
  StepType,
  RegistrationStepQuery,
  RegistrationStepService,
  LoggingService,
  ErrorStateService,
  MemberDetails,
  ProviderUiActionResponse,
  MemberDetailsRegistration,
  PaymentService,
  GiftCodeModel,
  GiftCodeType,
  GiftCodeEntitlementValueType,
  RegistrationPurchaseBasket,
  RegistrationBasketService,
  RegistrationBasketQuery,
  GiftCodeService,
  ProviderActionKind,
  MemberQuery,
  AddressQuery,
  AddressType,
  Address,
  RegistrationPurchaseBasketItem,
  CardTokenQuery,
  AuthService,
  AddressService,
  Country,
  AccountStatus,
  OrderItem,
  AdjustmentType,
  PaymentAdjustment,
  ExpressCheckoutResult,
  MarketplaceExpressCheckoutContext,
  ProviderName,
} from '@fgb/core';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';

@Component({
  selector: 'fgbcl-registration-payment',
  templateUrl: './registration-payment.component.html',
  styleUrls: ['./registration-payment.component.scss'],
})
export class RegistrationPaymentComponent implements OnInit, OnDestroy {
  readonly stepType = StepType.Payment;
  basket$: Observable<RegistrationPurchaseBasket>;
  basketItems: RegistrationPurchaseBasketItem[] = [];
  providerResponse?: ProviderUiActionResponse;
  editingBillingAddress: boolean = false;
  editingShippingAddress: boolean = false;
  paymentInitialised: boolean = false;
  billingAddress: Address | undefined;
  shippingAddress: Address | undefined;
  billingAddressForm: UntypedFormGroup;
  shippingAddressForm: UntypedFormGroup;
  giftCodeInput: string = '';
  totalBasketPrice: number;
  totalItemPrice: number;
  totalTaxPrice: number;
  totalShippingPrice: number;
  appliedGiftCodes: GiftCodeModel[] = [];
  giftCodeInputMade = false;
  emptyBasket: RegistrationPurchaseBasket = {
    Tax: 0,
    TotalPrice: 0,
    TotalPriceWithTaxAndShipping: 0,
    TotalPriceWithTax: 0,
    TotalShipping: 0,
    TotalTax: 0,
    GiftCodes: [],
    BasketItems: [],
  };
  currentUser: MemberDetails | undefined;
  addresses: Address[] = [];
  memberDetailsSubscription: Subscription;
  cardTokenCount: number = 0;
  payWithCardToken: boolean = false;
  giftCodeAccepted: boolean = false;
  isLoading: boolean = false;
  regions: any[];
  stepInProgress: boolean = false;

  /** Express wallet providers offered above the standard card flow. Set via route data. */
  @Input() expressCheckoutProviders: ProviderName[] = [ProviderName.FreedomPay, ProviderName.Paze, ProviderName.PayPal];

  /** Whether to offer express checkout on this step. */
  @Input() showExpressCheckout: boolean = true;

  /**
   * Context for the express checkout container, which pays the basket through the marketplace
   * express endpoints. Built once the basket is loaded, since the wallet purchase needs the items.
   */
  expressCheckoutContext: MarketplaceExpressCheckoutContext | undefined;

  @ViewChild('paymentModal', { static: true }) paymentModalRef: ElementRef;
  @ViewChild('unappliedGiftCodeModal', { static: true }) unappliedGiftCodeModalRef: ElementRef;
  postCodeRegex = /^\d{5}$/;

  constructor(
    private errorService: ErrorStateService,
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private registrationBasketService: RegistrationBasketService,
    private registrationBasketQuery: RegistrationBasketQuery,
    private logService: LoggingService,
    private paymentService: PaymentService,
    private giftCodeService: GiftCodeService,
    private formBuilder: UntypedFormBuilder,
    private memberQuery: MemberQuery,
    private addressQuery: AddressQuery,
    private addressService: AddressService,
    private cardTokenQuery: CardTokenQuery,
    private authService: AuthService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    // Initialize forms
    this._initializeForms();

    this.logService.info('Retrieving latest basket information...');
    this.registrationBasketService.fetchRegistrationBasketForCurrentWizard().toPromise();
    this.basket$ = this.registrationBasketQuery.select().pipe(
      tap((basket: any) => {
        if (this.appliedGiftCodes.length < 1) {
          this.basketItems = basket.BasketItems;
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        }
        // Rebuild after the items land, so the express context carries the real basket.
        this._buildExpressCheckoutContext();
      })
    ) as Observable<RegistrationPurchaseBasket>;

    this.memberDetailsSubscription = this.memberQuery.selectMemberDetails().subscribe((md) => {
      this.currentUser = md;
      if (this.currentUser) {
        this.addressQuery
          .selectAddresses([AddressType.DefaultAddress])
          .pipe(
            tap((addresses) => {
              this.addresses = addresses;
            })
          )
          .toPromise();
      }
      this.billingAddress = this._getBillingAddress();
      this.shippingAddress = this._getShippingAddress();
      // re-initialize forms with data
      this._initializeForms(this.billingAddress, this.shippingAddress);
    });

    if (this.authService.isAuthenticated()) {
      this.cardTokenQuery
        .selectCard()
        .pipe(tap((tokens) => (this.cardTokenCount = tokens.length)))
        .toPromise();
    }
  }

  private createOrders(basketItems: RegistrationPurchaseBasketItem[]): OrderItem[] {
    return (
      basketItems?.map(
        (item: any) =>
          new OrderItem(
            item?.MembershipOption?.ProductId,
            item?.MembershipOption?.DisplayName,
            1,
            item?.PriceWithTaxAndShipping,
            item?.DisplayImage,
            item?.DisplayText
          )
      ) || []
    );
  }

  /**
   * Build the context for the express checkout container, which drives api/register/express/* in
   * 'registration' mode. Mirrors the marketplace flow: the current basket items are posted with the
   * init/complete calls. Rebuilt on every basket emission so gift code and address recalculations
   * are reflected in what gets sent.
   */
  private _buildExpressCheckoutContext(): void {
    this.expressCheckoutContext = {
      mode: 'registration',
      giftCodes: this.appliedGiftCodes,
      // Registration always pays with the wallet's own credential, never a stored card token.
      useCardToken: false,
      successUrl: '/register/success',
      failureUrl: '/register/failure',
    };
  }

  private mapAdjustments(shipping: number, tax: number): PaymentAdjustment[] {
    return [
      { type: AdjustmentType.Shipping, amount: shipping, description: 'Shipping' },
      { type: AdjustmentType.Tax, amount: tax, description: 'Tax' },
    ].filter((adjustment) => adjustment.amount > 0);
  }

  ngOnDestroy() {
    if (this.memberDetailsSubscription) {
      this.memberDetailsSubscription.unsubscribe();
    }
  }

  back(): void {
    this.registrationStepService.navigateToPreviousStep(this.stepType);
  }

  openPaymentModal(template: any): Promise<any> {
    return this.modalService.open(template, {
      centered: true,
      backdrop: 'static',
      windowClass: 'registration-payment-modal',
    }).result;
  }

  closeModal(): void {
    this.modalService.dismissAll();
    this.paymentService.destroyPaymentComponent();
  }

  async submitStep() {
    this.isLoading = true;
    this.stepInProgress = true;
    this.errorService.clearErrors();
    this.shippingAddressForm.markAllAsTouched();
    this.billingAddressForm.markAllAsTouched();

    if (!this.shippingAddressForm.valid || !this.billingAddressForm.valid) {
      this.errorService.addError(
        `Please update your ${!this.billingAddressForm.valid ? 'billing' : ''} ${
          !this.shippingAddressForm.valid && !this.billingAddressForm.valid ? 'and' : ''
        } ${!this.shippingAddressForm.valid ? 'shipping' : ''} address`
      );
      // Clear the in-progress flags, otherwise the pay button stays spinning and disabled forever
      // after a validation failure (reachable from the express flow, which calls this directly).
      this.isLoading = false;
      this.stepInProgress = false;
      return;
    }

    if (this.shippingAddressForm.valid && this.billingAddressForm.valid) {
      if (this.giftCodeInputMade && !this.errorService.hasErrors()) {
        this.unappliedGiftCodeModal();
      } else {
        this.detectGiftCodeInput(this.giftCodeInput);
        if (!this.paymentInitialised && this.billingAddress && this.shippingAddress) {
          this.paymentInitialised = true;
          this.logService.info('Retrieving provider action response for payment request...');
          const callbackUrl: string = document.getElementsByTagName('base')[0].href + '/register/payment/result';

          const orders = this.createOrders(this.basketItems);
          const adjustments = this.mapAdjustments(this.totalShippingPrice, this.totalTaxPrice);
          const order = this.paymentService.createOrder(orders, adjustments, this.appliedGiftCodes);

          // Contact the API to get the provider action response
          await firstValueFrom(
            this.registrationStepService.makeRegistrationPayment(
              this.billingAddress,
              this.shippingAddress,
              callbackUrl,
              this.appliedGiftCodes,
              this.payWithCardToken
            )
          )
            .then((response) => {
              if (response.ProviderActionInformation.ActionKind == ProviderActionKind.None) {
                this.registrationStepService
                  .submitRegistrationStepData(this.stepType, this.emptyBasket, true, '/register/success')
                  .toPromise();
              } else {
                this.openPaymentModal(this.paymentModalRef).catch(() => (this.paymentInitialised = false));
                this.paymentService.createPayment(
                  order,
                  response,
                  'paymentdiv',
                  '/register/payment/result',
                  '/register/success',
                  '/register/payment'
                );
              }
            })
            .catch((error) => {
              this.logService.error(error);
              this.errorService.addError('An error occurred, please try again.');
              this.isLoading = false;
              this.paymentInitialised = false;
              this.stepInProgress = false;
            })
            .finally(() => {
              this.isLoading = false;
              this.paymentInitialised = false;
              this.stepInProgress = false;
            });
        }
      }
    }
  }

  /**
   * `register/express/complete` already rebuilt the basket, took the payment AND saved the
   * registration server-side, so by the time this fires the whole registration is finished. There is
   * therefore nothing left to submit: posting `register/step/46` here just gets a 404
   * 'Registration process already complete', and routing via /register/payment/result fails too,
   * because that page reconciles a provider redirect and infers the order from router.url (giving
   * "Order Id cannot be null" for a wallet payment that never populated it).
   *
   * So this only brings the client in step with the server: mark the step complete locally and refresh
   * the token so the new account_status lands. Navigation is NOT done here — the express checkout
   * container routes to the context's successUrl/failureUrl off the same `result` emission.
   */
  async onExpressCheckoutResult(result: ExpressCheckoutResult): Promise<void> {
    if (!result.success) {
      this.logService.error('Express registration payment failed.', result.error);
      return;
    }

    this.logService.info('Express registration payment succeeded and the registration is complete.', result.data);
    this.registrationStepService.setStepCompleteStatus(this.stepType, true);

    // The refreshed token carries the updated account_status: without it the success page routes
    // through the first time user guard on a stale status and drops the user back into the wizard.
    // Guarded because an express registration may complete before the user has any session yet.
    if (this.authService.hasCurrentRefreshToken()) {
      try {
        await firstValueFrom(this.authService.refreshAccessTokenAndContext());
      } catch (error) {
        this.logService.error('Failed to refresh the auth token after an express registration payment.', error);
      }
    }
  }

  /** Customer was ineligible or cancelled the wallet flow — leave the native card flow in place. */
  onExpressCheckoutIncomplete(_result: ExpressCheckoutResult): void {
    // No-op: the standard card payment flow remains available as the fallback.
  }

  /** Gets the regions of the country */
  onCountryChange(event: Country): void {
    this.regions = event?.Regions;
  }

  private _initializeForms(billing = {}, shipping = {}): void {
    this.billingAddressForm = this._generateBillingAddressForm(billing);
    this.shippingAddressForm = this._generateShippingAddressForm(shipping);
  }

  private _generateBillingAddressForm(billing: any): UntypedFormGroup {
    return this.formBuilder.group({
      // FirstName: [ billing.FirstName || '', [ Validators.required, FGBValidators.Empty ]],
      // Surname: [ billing.Surname || '', [ Validators.required, FGBValidators.Empty ]],
      Street: [billing.Street || '', [Validators.required, FGBValidators.Empty]],
      Town: [billing.Town || '', [Validators.required, FGBValidators.Empty]],
      County: [billing.County || '', [Validators.required, FGBValidators.Empty]],
      Postcode: [billing.Postcode || '', [Validators.required, Validators.pattern(this.postCodeRegex), FGBValidators.Empty]],
      Country: [billing.Country || '', [Validators.required, FGBValidators.Empty]],
      AddressType: [AddressType.BillingAddress1],
      HomeNumber: [billing.HomeNumber || '', [Validators.required, FGBValidators.Empty]],
    });
  }

  private _generateShippingAddressForm(shipping: any): UntypedFormGroup {
    return this.formBuilder.group({
      // FirstName: [ shipping.FirstName || '', [ Validators.required, FGBValidators.Empty ]],
      // Surname: [ shipping.Surname || '', [ Validators.required, FGBValidators.Empty ] ],
      Street: [shipping.Street || '', [Validators.required, FGBValidators.Empty]],
      Town: [shipping.Town || '', [Validators.required, FGBValidators.Empty]],
      County: [shipping.County || '', [Validators.required, FGBValidators.Empty]],
      Postcode: [shipping.Postcode || '', [Validators.required, Validators.pattern(this.postCodeRegex), FGBValidators.Empty]],
      Country: [shipping.Country || '', [Validators.required, FGBValidators.Empty]],
      AddressType: [AddressType.ShippingAddress1],
      HomeNumber: [shipping.HomeNumber || '', [Validators.required, FGBValidators.Empty]],
    });
  }

  /** Start editing the user's billing address. */
  editBillingAddress() {
    this.editingBillingAddress = true;
    this.logService.debug('Opening billing address form with initial value', this.billingAddress);
  }

  /** Start editing the user's shipping address. */
  editShippingAddress() {
    this.editingShippingAddress = true;
    this.logService.debug('Opening shipping address form with initial value', this.editingShippingAddress);
  }

  /* Cancel editing and revert back to initial value */
  cancelShippingForm(): void {
    this.editingShippingAddress = false;
    // re-initialize form with the initial value
    this.shippingAddressForm = this._generateShippingAddressForm(this.shippingAddress);
  }

  /* Cancel editing and revert back to initial value */
  cancelBillingForm(): void {
    this.editingBillingAddress = false;
    // re-initialize form with the initial value
    this.billingAddressForm = this._generateBillingAddressForm(this.billingAddress);
  }

  /** Update the user's billing address with the value of the billingAddressForm. */
  updateBillingAddress() {
    this.billingAddressForm.markAllAsTouched();

    if (this.billingAddressForm.valid) {
      this.billingAddress = { ...this.billingAddress, ...this.billingAddressForm.value };

      this.editingBillingAddress = false;

      if (this.authService.isAuthenticated() && this.memberQuery.getUserContext().status !== AccountStatus.FirstTimeUser) {
        this.addressService.saveAddress(this.billingAddress!);
      }
    }
  }

  async updateShippingAddress() {
    this.shippingAddressForm.markAllAsTouched();

    if (this.shippingAddressForm.valid) {
      this.shippingAddress = { ...this.shippingAddress, ...this.shippingAddressForm.value };

      this.editingShippingAddress = false;
      if (this.authService.isAuthenticated() && this.memberQuery.getUserContext().status !== AccountStatus.FirstTimeUser) {
        this.addressService.saveAddress(this.shippingAddress!);
      }
    }
  }

  /** Gets the user's address from the personal details step data. */
  private _getDefaultBillingAddress(): Address | undefined {
    const personalDetailsStep = this.registrationStepQuery.getStep(StepType.PersonalDetails);
    if (personalDetailsStep) {
      const personalDetails = personalDetailsStep.Data as MemberDetailsRegistration;
      return {
        AddressName: 'Billing Address',
        FirstName: personalDetails.FirstName,
        Surname: personalDetails.Surname,
        Street: personalDetails.Street,
        Town: personalDetails.Town,
        County: personalDetails.County,
        Postcode: personalDetails.PostCode,
        Country: personalDetails.Country,
        HomeNumber: personalDetails.HomeNumber,
        EmailAddress: personalDetails.EmailAddress1,
        PortalId: personalDetails.PortalId,
        ClubId: personalDetails.ClubId,
      } as Address;
    }
    if (this.addresses) {
      let billingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress1)[0] as Address;
      if (billingAddress1) {
        return billingAddress1;
      }

      let billingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress2)[0] as Address;
      if (billingAddress2) {
        return billingAddress2;
      }
    }
    if (this.currentUser) {
      return {
        AddressName: 'Billing Address',
        FirstName: this.currentUser.FirstName,
        Surname: this.currentUser.Surname,
        Street: this.currentUser.Street,
        Town: this.currentUser.Town,
        County: this.currentUser.County,
        Postcode: this.currentUser.PostCode,
        Country: this.currentUser.Country,
        HomeNumber: this.currentUser.HomeNumber,
        EmailAddress: this.currentUser.EmailAddress1,
        PortalId: this.currentUser.PortalId,
        ClubId: this.currentUser.ClubId,
      } as Address;
    }
    return;
  }

  private _getDefaultShippingAddress(): Address | undefined {
    const personalDetailsStep = this.registrationStepQuery.getStep(StepType.PersonalDetails);
    if (personalDetailsStep) {
      const personalDetails = personalDetailsStep.Data as MemberDetailsRegistration;
      return {
        AddressName: 'Billing Address',
        FirstName: personalDetails.FirstName,
        Surname: personalDetails.Surname,
        Street: personalDetails.Street,
        Town: personalDetails.Town,
        County: personalDetails.County,
        Postcode: personalDetails.PostCode,
        Country: personalDetails.Country,
        HomeNumber: personalDetails.HomeNumber,
        EmailAddress: personalDetails.EmailAddress1,
        PortalId: personalDetails.PortalId,
        ClubId: personalDetails.ClubId,
      } as Address;
    }
    if (this.addresses) {
      let shippingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress1)[0] as Address;
      if (shippingAddress1) {
        return shippingAddress1;
      }

      let shippingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress2)[0] as Address;
      if (shippingAddress2) {
        return shippingAddress2;
      }
    }
    if (this.currentUser) {
      return {
        AddressName: 'Billing Address',
        FirstName: this.currentUser.FirstName,
        Surname: this.currentUser.Surname,
        Street: this.currentUser.Street,
        Town: this.currentUser.Town,
        County: this.currentUser.County,
        Postcode: this.currentUser.PostCode,
        Country: this.currentUser.Country,
        HomeNumber: this.currentUser.HomeNumber,
        EmailAddress: this.currentUser.EmailAddress1,
        PortalId: this.currentUser.PortalId,
        ClubId: this.currentUser.ClubId,
      } as Address;
    }
    return;
  }

  /** Gets the user's billing address from the session storage, or the personal details step if not found. */
  private _getBillingAddress(): Address | undefined {
    try {
      const billingAddressFromSession = sessionStorage.getItem('BillingAddress');
      if (billingAddressFromSession) {
        this.logService.info('Using billing address from session storage');
        return JSON.parse(billingAddressFromSession);
      }
    } catch {
      this.logService.error('Failed to retrieve billing address from session storage, using default address instead...');
    }

    return this._getDefaultBillingAddress();
  }

  /** Gets the user's shipping address from the shipping address step data, or the personal details step if not provided. */
  private _getShippingAddress(): Address | undefined {
    const shippingAddressStep = this.registrationStepQuery.getStep(StepType.ShippingAddress);
    if (shippingAddressStep && shippingAddressStep.Data) {
      return shippingAddressStep.Data as Address;
    }

    return this._getDefaultShippingAddress();
  }

  /** Takes in a gift code string, checks to see if the code is valid and returns the calculated discount */
  applyGiftCode() {
    this.errorService.clearErrors();
    this.giftCodeAccepted = false;

    if (!this.giftCodeInput) {
      this.errorService.addError('Gift Code cannot be empty');
      return;
    }

    if (!!this.appliedGiftCodes.filter((code) => code.Code === this.giftCodeInput).length) {
      this.errorService.addError('This gift code has already been applied');
      return;
    }

    if (this.totalBasketPrice === 0) {
      this.errorService.addError('Gift code cannot be applied if the basket value is 0');
      return;
    }

    if (this.appliedGiftCodes.length > 2) {
      this.errorService.addError('The maximum amount of gift codes have been applied');
      return;
    }

    firstValueFrom(this.giftCodeService.validateGiftCode(this.giftCodeInput))
      .then((giftCode) => {
        if (
          giftCode.GiftCodeAssignType == GiftCodeType.Product &&
          !this.basketItems.filter(
            (item) =>
              item.MembershipOption.ProductId == giftCode.EntitlementItemId ||
              item.MembershipOption.ProductId == giftCode.EntitlementItemId2
          ).length
        ) {
          this.errorService.addError('Gift code does not apply to any of the basket items.');
          return;
        } else if (
          giftCode.EntitlementValueType == GiftCodeEntitlementValueType.Percentage &&
          !!this.appliedGiftCodes.filter((gc) => gc.EntitlementValueType == GiftCodeEntitlementValueType.Percentage).length
        ) {
          this.errorService.addError('Only one percentage discount gift code can applied.');
          return;
        } else if (giftCode.GiftCodeStatus != 0) {
          this.errorService.addError('Gift code status not avalaible.');
          return;
        } else {
          this.giftCodeAccepted = true;
          this.giftCodeInput = '';
          this.giftCodeInputMade = false;
        }

        if (this.appliedGiftCodes.length > 0) {
          /** reset basket values and recalculate with added giftcodes */
          this.resetBasketValues();
        }

        if (giftCode.EntitlementValueType === GiftCodeEntitlementValueType.Value) {
          this.appliedGiftCodes.unshift(giftCode);
        } else {
          this.appliedGiftCodes.push(giftCode);
        }

        this.appliedGiftCodes.forEach((x) => {
          this._calculateGiftCodeDeduction(x);
        });
      })
      .catch((err) => this.removeUnappliedGiftCode());
  }

  /** Calculate the new total basket price after Gift Code has been applied */
  private _calculateGiftCodeDeduction(giftCode: GiftCodeModel) {
    this.errorService.clearErrors();
    let discount = 0;

    if (giftCode.GiftCodeAssignType === GiftCodeType.Order) {
      switch (giftCode.EntitlementValueType) {
        // Value
        case GiftCodeEntitlementValueType.Value: {
          discount = giftCode.EntitlementValue;
          break;
        }
        case GiftCodeEntitlementValueType.Percentage: {
          // Percentage
          discount = (giftCode.EntitlementValue / 100) * this.totalBasketPrice;
          break;
        }
        case GiftCodeEntitlementValueType.NumberOfEntitlements: {
          // Number of Items - Only used for Free
          this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x.Code === giftCode.Code);
          discount = this.totalBasketPrice;
          break;
        }
        default: {
          this.errorService.addError('Invalid Gift Code');
          break;
        }
      }
    } else if (giftCode.GiftCodeAssignType === GiftCodeType.Shipping) {
      if (this.totalShippingPrice === 0) {
        this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x.Code !== giftCode.Code);
        this.errorService.addError('Gift Code cannot be applied if the shipping is already 0');
        return;
      }

      switch (giftCode.EntitlementValueType) {
        case GiftCodeEntitlementValueType.Value: {
          // Value
          discount = giftCode.EntitlementValue;
          break;
        }
        case GiftCodeEntitlementValueType.Percentage: {
          // Percentage
          discount = (giftCode.EntitlementValue / 100) * this.totalShippingPrice;
          break;
        }
        case GiftCodeEntitlementValueType.NumberOfEntitlements: {
          // Number of Items - Only used for Free
          discount = this.totalShippingPrice;
          break;
        }
        default: {
          this.errorService.addError('Invalid Gift Code');
          break;
        }
      }

      this.totalShippingPrice = Math.max(0, this.totalShippingPrice - discount);
    } else if (giftCode.GiftCodeAssignType == GiftCodeType.Product) {
      var numberOfTimesApplied = 0;
      this.basketItems
        .filter(
          (item) =>
            item.MembershipOption.ProductId == giftCode.EntitlementItemId ||
            item.MembershipOption.ProductId == giftCode.EntitlementItemId2
        )
        .forEach((item) => {
          if (numberOfTimesApplied < giftCode.EntitlementValue) {
            numberOfTimesApplied++;
            discount += item.PriceWithTaxAndShipping;
          }
        });

      this.totalItemPrice = Math.max(0, this.totalItemPrice - discount);
    }
    this.totalBasketPrice = Math.max(0, this.totalBasketPrice - discount);
  }

  resetBasketValues() {
    this.registrationBasketQuery
      .select()
      .pipe(
        tap((basket) => {
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        })
      )
      .toPromise();
  }

  /** Works out the total basket price after removal of gift code */
  removeGiftCode(code: string) {
    this.errorService.clearErrors();
    let giftCode = this.appliedGiftCodes.find((x) => x.Code === code);

    if (giftCode) {
      this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x !== giftCode);

      /** reset basket values and recalculate with remaining giftcodes */
      this.resetBasketValues();

      if (this.appliedGiftCodes.length > 0) {
        this.appliedGiftCodes.forEach((x) => {
          this._calculateGiftCodeDeduction(x);
        });
      }
    }
  }

  detectGiftCodeInput(input: string) {
    this.giftCodeInputMade = false;
    if (/\S/.test(input)) {
      this.errorService.clearErrors();
      this.giftCodeInputMade = true;
    }
  }

  unappliedGiftCodeModal() {
    this.modalService.open(this.unappliedGiftCodeModalRef, {
      centered: true,
      windowClass: 'registration-payment-unapplied-giftcode-modal',
    });
  }

  removeUnappliedGiftCode() {
    this.giftCodeInput = '';
    this.giftCodeInputMade = false;
  }
}
